import React, { useEffect, useState } from "react";
import { Loader2, Trash2, Download, ArrowLeft, Plus, X, ClipboardList, Share2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import ImageUpload from "./ImageUpload";
import { MetricoolComposer } from "./MetricoolComposer";
import { MetricoolRecurringButton } from "./RecurringPromos";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "");

const NudgeSettings = () => {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { adminApi.assessmentNudge.get().then(setCfg).catch(() => {}); }, []);
  if (!cfg) return null;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await adminApi.assessmentNudge.save(cfg); toast({ title: "Nudge settings saved" }); }
    catch { toast({ title: "Couldn't save", variant: "destructive" }); }
    finally { setSaving(false); }
  };
  const ip = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm";
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-6 overflow-hidden" data-testid="assessment-nudge-card">
      <div className="flex items-center justify-between gap-3 p-5">
        <div>
          <h3 className="font-display font-bold text-navy">Low-score follow-up email</h3>
          <p className="text-xs text-slate-500 mt-0.5">Automatically emails people who score below your threshold a few days later, offering a free results chat. Only sent to those who opted in to marketing.</p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 cursor-pointer shrink-0">
          <input type="checkbox" data-testid="nudge-enabled" checked={!!cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4 rounded text-teal" /> {cfg.enabled ? "On" : "Off"}
        </label>
      </div>
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left px-5 pb-3 text-xs font-semibold text-teal" data-testid="nudge-toggle-editor">{open ? "Hide message settings" : "Edit message & timing"}</button>
      {open && (
        <div className="px-5 pb-5 space-y-3 border-t border-slate-100 pt-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-xs font-semibold text-slate-500">Send after (days)</span>
              <input type="number" min="1" data-testid="nudge-delay" value={cfg.delay_days} onChange={(e) => set("delay_days", Number(e.target.value))} className={`${ip} mt-1`} /></label>
            <label className="block"><span className="text-xs font-semibold text-slate-500">Score threshold (send if below)</span>
              <input type="number" min="1" max="100" data-testid="nudge-threshold" value={cfg.score_threshold} onChange={(e) => set("score_threshold", Number(e.target.value))} className={`${ip} mt-1`} /></label>
          </div>
          <label className="block"><span className="text-xs font-semibold text-slate-500">Subject</span>
            <input data-testid="nudge-subject" value={cfg.subject} onChange={(e) => set("subject", e.target.value)} className={`${ip} mt-1`} /></label>
          <label className="block"><span className="text-xs font-semibold text-slate-500">Message (one paragraph per line)</span>
            <textarea rows={5} data-testid="nudge-body" value={cfg.body} onChange={(e) => set("body", e.target.value)} className={`${ip} mt-1`} /></label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-xs font-semibold text-slate-500">Button label</span>
              <input data-testid="nudge-cta-label" value={cfg.cta_label} onChange={(e) => set("cta_label", e.target.value)} className={`${ip} mt-1`} /></label>
            <label className="block"><span className="text-xs font-semibold text-slate-500">Button link</span>
              <input data-testid="nudge-cta-link" value={cfg.cta_link} onChange={(e) => set("cta_link", e.target.value)} className={`${ip} mt-1`} /></label>
          </div>
        </div>
      )}
      <div className="px-5 pb-5">
        <button onClick={save} disabled={saving} data-testid="nudge-save" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold text-sm px-5 py-2 rounded-full">
          {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
        </button>
      </div>
    </div>
  );
};

export default function AssessmentManager() {
  const { toast } = useToast();
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null); // slug
  const [view, setView] = useState("submissions"); // submissions | config
  const [leads, setLeads] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState(null); // assessment being promoted to Metricool

  const load = () => adminApi.assessments.list().then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const open = async (slug, v = "submissions") => {
    setActive(slug); setView(v); setLoading(true);
    try {
      if (v === "submissions") {
        const [ld, fn] = await Promise.all([adminApi.assessments.leads(slug), adminApi.assessments.funnel(slug).catch(() => null)]);
        setLeads(ld); setFunnel(fn);
      } else setCfg(await adminApi.assessments.get(slug));
    } finally { setLoading(false); }
  };

  const toggleEnabled = async (a) => {
    const c = await adminApi.assessments.get(a.slug);
    await adminApi.assessments.save(a.slug, { ...c, enabled: !a.enabled });
    toast({ title: `${a.title} ${!a.enabled ? "enabled" : "disabled"}` });
    load();
  };

  const saveCfg = async () => {
    setSaving(true);
    try { await adminApi.assessments.save(active, cfg); toast({ title: "Assessment saved" }); load(); }
    catch { toast({ title: "Couldn't save", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const delLead = async (id) => { await adminApi.assessments.deleteLead(active, id); setLeads((l) => l.filter((x) => x.id !== id)); };

  const exportCsv = () => {
    const cols = ["name", "email", "business_name", "phone", "overall_score", "band_label", "marketing_consent", "created_at"];
    const head = ["Name", "Email", "Business", "Phone", "Score", "Result", "Marketing consent", "Date"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = leads.map((r) => cols.map((c) => c === "created_at" ? fmt(r[c]) : c === "marketing_consent" ? (r[c] ? "Yes" : "No") : r[c]).map(esc).join(","));
    const csv = [head.map(esc).join(","), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${active}-submissions.csv`; a.click();
  };

  if (!list) return <div className="py-16 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>;

  // ---- List view ----
  if (!active) {
    return (
      <div data-testid="assessment-manager">
        <h2 className="font-display font-extrabold text-2xl text-navy mb-1">Business Assessment Tools</h2>
        <p className="text-sm text-slate-500 mb-6">Review submissions, tweak questions and weightings, edit result wording, and switch each tool on or off.</p>
        <NudgeSettings />
        <div className="grid md:grid-cols-3 gap-4">
          {list.map((a) => (
            <div key={a.slug} data-testid={`assess-card-${a.slug}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-center justify-between">
                <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${a.enabled ? "bg-teal/10 text-teal" : "bg-slate-100 text-slate-400"}`}><ClipboardList size={17} /></span>
                <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 cursor-pointer">
                  <input type="checkbox" data-testid={`assess-toggle-${a.slug}`} checked={a.enabled} onChange={() => toggleEnabled(a)} className="h-4 w-4 rounded text-teal" /> {a.enabled ? "Live" : "Off"}
                </label>
              </div>
              <h3 className="font-display font-bold text-navy mt-3">{a.title}</h3>
              <p className="text-sm text-slate-400">{a.submissions} submission{a.submissions === 1 ? "" : "s"}</p>
              <div className="flex gap-2 mt-4">
                <button data-testid={`assess-view-${a.slug}`} onClick={() => open(a.slug, "submissions")} className="flex-1 bg-navy text-white text-sm font-semibold py-2 rounded-full hover:bg-navy/90">Submissions</button>
                <button data-testid={`assess-edit-${a.slug}`} onClick={() => open(a.slug, "config")} className="flex-1 border border-slate-200 text-navy text-sm font-semibold py-2 rounded-full hover:bg-slate-50">Edit</button>
              </div>
              <button
                data-testid={`assess-metricool-${a.slug}`}
                onClick={() => setPromo(a)}
                className="w-full mt-2 inline-flex items-center justify-center gap-1.5 bg-slate-100 text-navy text-sm font-semibold py-2 rounded-full hover:bg-slate-200"
              >
                <Share2 size={14} /> Send to Metricool
              </button>
              <div className="mt-2">
                <MetricoolRecurringButton
                  className="w-full inline-flex items-center justify-center gap-1.5 bg-slate-100 text-navy text-sm font-semibold py-2 rounded-full hover:bg-slate-200"
                  label="Recurring promo"
                  initial={{
                    text: `${a.title}\n\n${typeof window !== "undefined" ? window.location.origin : ""}/api/share/assessment/${a.slug}`,
                    title: a.title || "",
                    summary: `A free, quick online self-assessment for business owners: ${a.title}. ${a.tagline || ""}`.trim(),
                    link: `${typeof window !== "undefined" ? window.location.origin : ""}/api/share/assessment/${a.slug}`,
                    kind: "assessment", autogen: true, source: "assessment", source_id: a.slug,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        {promo && (
          <MetricoolComposer
            open={!!promo}
            onClose={() => setPromo(null)}
            initial={{
              text: `${promo.title}\n\n${typeof window !== "undefined" ? window.location.origin : ""}/api/share/assessment/${promo.slug}`,
              title: promo.title || "",
              summary: `A free, quick online self-assessment for business owners: ${promo.title}. ${promo.tagline || ""}`.trim(),
              link: `${typeof window !== "undefined" ? window.location.origin : ""}/api/share/assessment/${promo.slug}`,
              kind: "assessment", autogen: true, source: "assessment", source_id: promo.slug,
            }}
          />
        )}
      </div>
    );
  }

  const meta = list.find((a) => a.slug === active);

  // ---- Submissions view ----
  if (view === "submissions") {
    return (
      <div data-testid="assessment-submissions">
        <button onClick={() => setActive(null)} className="text-sm text-navy inline-flex items-center gap-1 mb-4 hover:underline"><ArrowLeft size={14} /> All tools</button>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-extrabold text-xl text-navy">{meta?.title} — submissions</h2>
          <button onClick={exportCsv} disabled={!leads.length} className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-200 px-4 py-2 rounded-full hover:bg-slate-50 disabled:opacity-40"><Download size={15} /> Export CSV</button>
        </div>
        {funnel && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-5" data-testid="assessment-funnel">
            <h3 className="font-display font-bold text-navy mb-1">Completion funnel</h3>
            <p className="text-xs text-slate-400 mb-4">Where visitors progress and drop off. Percentages are of the people who started.</p>
            {(() => {
              const starts = funnel.starts || 0;
              const rows = [
                { label: "Started", count: starts },
                ...funnel.sections.map((s, i) => ({ label: `Completed: ${s.title}`, count: s.count })),
                { label: "Submitted (got results)", count: funnel.submits || 0 },
              ];
              const pct = (n) => (starts ? Math.min(100, Math.round((n / starts) * 100)) : 0);
              return (
                <div className="space-y-2">
                  {rows.map((r, i) => {
                    const prev = i === 0 ? r.count : rows[i - 1].count;
                    const drop = prev > 0 ? Math.max(0, Math.round(((prev - r.count) / prev) * 100)) : 0;
                    return (
                      <div key={i} data-testid={`funnel-row-${i}`}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-600">{r.label}</span>
                          <span className="font-semibold text-navy">{r.count} <span className="text-slate-400 font-normal">({pct(r.count)}%)</span>{i > 0 && drop > 0 && <span className="text-red-400 ml-2">−{drop}%</span>}</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-teal" style={{ width: `${pct(r.count)}%` }} /></div>
                      </div>
                    );
                  })}
                  {starts === 0 && <p className="text-xs text-slate-400">No funnel data captured yet.</p>}
                </div>
              );
            })()}
          </div>
        )}
        {leads.length > 0 && (() => {
          const counts = {};
          leads.forEach((l) => {
            let w = l.weakest_category;
            if (!w && Array.isArray(l.sections) && l.sections.length) {
              w = [...l.sections].sort((a, b) => (a.score || 0) - (b.score || 0))[0]?.title;
            }
            if (w) counts[w] = (counts[w] || 0) + 1;
          });
          const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);
          if (!ranked.length) return null;
          const max = ranked[0][1];
          return (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-5" data-testid="weakest-category-chart">
              <h3 className="font-display font-bold text-navy mb-1">Where prospects struggle most</h3>
              <p className="text-xs text-slate-400 mb-4">The weakest category across all {leads.length} submissions.</p>
              <div className="space-y-2.5">
                {ranked.map(([title, n], i) => (
                  <div key={title} data-testid={`weakest-row-${i}`}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-600">{title}</span>
                      <span className="font-semibold text-navy">{n} <span className="text-slate-400 font-normal">({Math.round((n / leads.length) * 100)}%)</span></span>
                    </div>
                    <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-orange" style={{ width: `${Math.round((n / max) * 100)}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        {loading ? <Loader2 className="animate-spin" /> : leads.length === 0 ? <p className="text-slate-400 text-sm">No submissions yet.</p> : (
          <div className="overflow-x-auto bg-white rounded-2xl border border-slate-100">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                {["Name", "Email", "Business", "Score", "Result", "Consent", "Date", ""].map((h) => <th key={h} className="py-2 px-3 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {leads.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50">
                    <td className="py-2 px-3 text-navy">{r.name}</td>
                    <td className="py-2 px-3 text-slate-500">{r.email}</td>
                    <td className="py-2 px-3 text-slate-500">{r.business_name || "-"}</td>
                    <td className="py-2 px-3 font-bold text-navy">{r.overall_score}</td>
                    <td className="py-2 px-3 text-slate-500">{r.band_label}</td>
                    <td className="py-2 px-3">{r.marketing_consent ? <span className="text-emerald-600 font-semibold">Yes</span> : <span className="text-slate-400">No</span>}</td>
                    <td className="py-2 px-3 text-slate-400">{fmt(r.created_at)}</td>
                    <td className="py-2 px-3"><button onClick={() => delLead(r.id)} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ---- Config editor ----
  if (!cfg) return <div className="py-10"><Loader2 className="animate-spin" /></div>;
  const upd = (patch) => setCfg({ ...cfg, ...patch });
  const updSection = (i, patch) => { const s = [...cfg.sections]; s[i] = { ...s[i], ...patch }; upd({ sections: s }); };
  const updQ = (si, qi, patch) => { const s = [...cfg.sections]; const q = [...s[si].questions]; q[qi] = { ...q[qi], ...patch }; s[si] = { ...s[si], questions: q }; upd({ sections: s }); };
  const addQ = (si) => { const s = [...cfg.sections]; s[si] = { ...s[si], questions: [...s[si].questions, { text: "", help: "" }] }; upd({ sections: s }); };
  const rmQ = (si, qi) => { const s = [...cfg.sections]; s[si] = { ...s[si], questions: s[si].questions.filter((_, i) => i !== qi) }; upd({ sections: s }); };
  const faqs = cfg.faqs || [];
  const updFaq = (i, patch) => { const arr = [...faqs]; arr[i] = { ...arr[i], ...patch }; upd({ faqs: arr }); };
  const addFaq = () => upd({ faqs: [...faqs, { question: "", answer: "" }] });
  const rmFaq = (i) => upd({ faqs: faqs.filter((_, x) => x !== i) });
  const ip = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm";

  return (
    <div data-testid="assessment-config">
      <button onClick={() => setActive(null)} className="text-sm text-navy inline-flex items-center gap-1 mb-4 hover:underline"><ArrowLeft size={14} /> All tools</button>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-extrabold text-xl text-navy">Edit {cfg.title}</h2>
        <button data-testid="assess-save" onClick={saveCfg} disabled={saving} className="bg-orange hover:bg-orange-dark text-white font-bold px-6 py-2.5 rounded-full disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3 mb-5">
        <label className="text-xs font-semibold text-slate-500">Introduction</label>
        <textarea value={cfg.intro} onChange={(e) => upd({ intro: e.target.value })} rows={3} className={ip} />
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs font-semibold text-slate-500">Estimated time</label><input value={cfg.est_time} onChange={(e) => upd({ est_time: e.target.value })} className={ip} /></div>
          <label className="flex items-center gap-2 text-sm mt-5"><input type="checkbox" checked={cfg.enabled !== false} onChange={(e) => upd({ enabled: e.target.checked })} className="h-4 w-4 rounded text-teal" /> Live on the website</label>
        </div>
        <div className="pt-2 border-t border-slate-100 mt-2">
          <ImageUpload
            label="Hero image (optional)"
            hint="Shown alongside the title at the top of this assessment page. Leave empty for the plain navy hero."
            value={cfg.hero_image || ""}
            onChange={(v) => upd({ hero_image: v })}
            altValue={cfg.hero_image_alt || ""}
            onAltChange={(v) => upd({ hero_image_alt: v })}
          />
        </div>
      </div>

      {cfg.sections.map((s, si) => (
        <div key={s.key} className="bg-white rounded-2xl border border-slate-100 p-5 mb-4">
          <div className="grid sm:grid-cols-4 gap-3 mb-3">
            <div className="sm:col-span-3"><label className="text-xs font-semibold text-slate-500">Section title</label><input value={s.title} onChange={(e) => updSection(si, { title: e.target.value })} className={ip} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Weight</label><input type="number" min="1" value={s.weight} onChange={(e) => updSection(si, { weight: Number(e.target.value) })} className={ip} /></div>
          </div>
          <div className="space-y-2">
            {s.questions.map((q, qi) => (
              <div key={qi} className="flex gap-2 items-start border border-slate-100 rounded-xl p-3">
                <div className="flex-1 space-y-1">
                  <input value={q.text} onChange={(e) => updQ(si, qi, { text: e.target.value })} placeholder="Question statement" className={ip} />
                  <input value={q.help || ""} onChange={(e) => updQ(si, qi, { help: e.target.value })} placeholder="Helper text (optional)" className={ip + " text-xs"} />
                  <div className="grid sm:grid-cols-3 gap-2 pt-1">
                    <textarea value={q.risk || ""} onChange={(e) => updQ(si, qi, { risk: e.target.value })} placeholder="Risk if they disagree (shown in priority risks)" rows={2} className={ip + " text-xs"} />
                    <textarea value={q.action || ""} onChange={(e) => updQ(si, qi, { action: e.target.value })} placeholder="Recommended action (shown in the plan)" rows={2} className={ip + " text-xs"} />
                    <input value={q.service || ""} onChange={(e) => updQ(si, qi, { service: e.target.value })} placeholder="CASS service" className={ip + " text-xs"} />
                  </div>
                  {active === "financial-awareness-score" && (
                    <div className="grid sm:grid-cols-2 gap-2 pt-2 mt-1 border-t border-slate-100">
                      <input value={q.number || ""} onChange={(e) => updQ(si, qi, { number: e.target.value })} placeholder="Number name (e.g. Cash in the Bank)" className={ip + " text-xs"} />
                      <textarea value={q.why || ""} onChange={(e) => updQ(si, qi, { why: e.target.value })} placeholder="Why this number matters (shown in the breakdown)" rows={2} className={ip + " text-xs sm:col-span-2"} />
                      <div className="sm:col-span-2">
                        <ImageUpload label="Illustration for this number" hint="Shown next to this number in the results breakdown." value={q.image || ""} onChange={(v) => updQ(si, qi, { image: v })} />
                      </div>
                    </div>
                  )}
                </div>
                <button onClick={() => rmQ(si, qi)} className="text-red-400 hover:text-red-600 mt-2"><X size={15} /></button>
              </div>
            ))}
            <button onClick={() => addQ(si)} className="text-xs font-semibold text-teal inline-flex items-center gap-1"><Plus size={13} /> Add question</button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-4 pt-3 border-t border-slate-100">
            <div><label className="text-xs font-semibold text-slate-500">Risk (if weak)</label><textarea value={s.risk || ""} onChange={(e) => updSection(si, { risk: e.target.value })} rows={2} className={ip} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Recommended action</label><textarea value={s.action || ""} onChange={(e) => updSection(si, { action: e.target.value })} rows={2} className={ip} /></div>
            <div><label className="text-xs font-semibold text-slate-500">CASS service</label><input value={s.service || ""} onChange={(e) => updSection(si, { service: e.target.value })} className={ip} /></div>
          </div>
        </div>
      ))}

      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h3 className="font-display font-bold text-navy mb-3">Result bands</h3>
        {cfg.bands.map((b, bi) => (
          <div key={bi} className="grid sm:grid-cols-6 gap-2 mb-2 items-center">
            <span className="text-xs text-slate-400">{b.min}–{b.max}</span>
            <input value={b.label} onChange={(e) => { const arr = [...cfg.bands]; arr[bi] = { ...b, label: e.target.value }; upd({ bands: arr }); }} className={ip + " sm:col-span-2"} />
            <input value={b.summary} onChange={(e) => { const arr = [...cfg.bands]; arr[bi] = { ...b, summary: e.target.value }; upd({ bands: arr }); }} className={ip + " sm:col-span-3"} />
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 mt-5" data-testid="assess-faqs-editor">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-navy">FAQs</h3>
          <button onClick={addFaq} className="text-xs font-semibold text-teal inline-flex items-center gap-1"><Plus size={13} /> Add FAQ</button>
        </div>
        <p className="text-xs text-slate-400 mb-4">Shown on the assessment page and used for search-engine FAQ rich results.</p>
        {faqs.length === 0 && <p className="text-sm text-slate-400">No FAQs yet.</p>}
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={i} className="flex gap-2 items-start">
              <div className="flex-1 space-y-1">
                <input value={f.question || ""} onChange={(e) => updFaq(i, { question: e.target.value })} placeholder="Question" className={ip} />
                <textarea value={f.answer || ""} onChange={(e) => updFaq(i, { answer: e.target.value })} placeholder="Answer" rows={2} className={ip} />
              </div>
              <button onClick={() => rmFaq(i)} className="text-red-400 hover:text-red-600 mt-2"><X size={15} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
